import React, { useState } from 'react';
import { Button, ButtonToolbar, Modal } from 'react-bootstrap';

/**
 * Configuration for a single bulk action.
 *
 *  - `applicableWhen` is the per-item predicate that drives mode-switching
 *    (spec §5): the toolbar only shows the action if at least one selected
 *    item satisfies it, and the count rendered next to the label reflects
 *    the size of that applicable subset. The action's `onClick` receives
 *    only the applicable subset, so callers do not need to re-filter.
 *
 *  - `confirm` is optional. When omitted, clicking the action button calls
 *    `onClick(applicableSubset)` directly. When provided, clicking opens a
 *    Bootstrap modal with the configured title and the body produced by
 *    `body(count)` so callers can interpolate the subset size into the
 *    consequences shown to the user. Confirming fires `onClick`; cancelling
 *    closes the modal without invoking it.
 */
export interface BulkAction<T> {
  /** Stable key for React reconciliation and per-action state tracking. */
  key: string;
  /** Base label rendered in the button (count is appended automatically). */
  label: string;
  /** Predicate filtering which selected items this action applies to. */
  applicableWhen: (item: T) => boolean;
  /** Called with the applicable subset (after confirm if `confirm` is set). */
  onClick: (items: T[]) => void | Promise<void>;
  /** Bootstrap variant for the action button (e.g. 'danger', 'success'). */
  variant?: string;
  /** Optional confirmation dialog. Omit for one-click actions (e.g. restore). */
  confirm?: {
    title: string;
    /** Receives the applicable subset count so callers can interpolate. */
    body: (count: number) => string;
    confirmLabel?: string;
    cancelLabel?: string;
    /** Bootstrap variant for the confirm button (defaults to action.variant). */
    variant?: string;
  };
}

export interface BulkActionToolbarProps<T> {
  /** Currently selected items. The toolbar renders nothing when empty. */
  selectedItems: T[];
  /** Action configs. Each is rendered only if at least one item matches. */
  actions: BulkAction<T>[];
  /** Called by the toolbar's Clear button. */
  onClearSelection: () => void;
  /** Override the default 'Clear selection' label. */
  clearLabel?: string;
}

/**
 * Shared bulk-action toolbar used by list pages (Programs, Sessions, etc.).
 * Implements the mode-switching display contract from spec §5: each action's
 * label includes the size of its applicable subset, and an action with no
 * applicable items is hidden. A "Clear selection" affordance is always
 * shown when the toolbar is visible.
 */
function BulkActionToolbar<T>({
  selectedItems,
  actions,
  onClearSelection,
  clearLabel,
}: BulkActionToolbarProps<T>) {
  const [confirmKey, setConfirmKey] = useState<string | null>(null);

  if (!Array.isArray(selectedItems) || selectedItems.length === 0) {
    return null;
  }

  // Pre-compute applicable subsets so render and click handlers agree.
  const applicableByKey: Record<string, T[]> = {};
  for (const action of actions) {
    applicableByKey[action.key] = selectedItems.filter(action.applicableWhen);
  }

  const visibleActions = actions.filter(a => applicableByKey[a.key].length > 0);
  const activeConfirmAction = confirmKey
    ? actions.find(a => a.key === confirmKey) ?? null
    : null;
  const activeConfirmItems = activeConfirmAction
    ? applicableByKey[activeConfirmAction.key]
    : [];

  const handleActionClick = (action: BulkAction<T>) => {
    if (action.confirm) {
      setConfirmKey(action.key);
      return;
    }
    action.onClick(applicableByKey[action.key]);
  };

  const handleConfirm = async () => {
    if (!activeConfirmAction) return;
    try {
      await activeConfirmAction.onClick(activeConfirmItems);
    } finally {
      setConfirmKey(null);
    }
  };

  return (
    <>
      <ButtonToolbar
        role="toolbar"
        aria-label="Bulk actions"
        className="d-flex align-items-center gap-2"
      >
        {visibleActions.map(action => (
          <Button
            key={action.key}
            variant={action.variant ?? 'primary'}
            size="sm"
            onClick={() => handleActionClick(action)}
          >
            {action.label} ({applicableByKey[action.key].length})
          </Button>
        ))}
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={onClearSelection}
        >
          {clearLabel ?? 'Clear selection'}
        </Button>
      </ButtonToolbar>

      {activeConfirmAction && activeConfirmAction.confirm && (
        <Modal show onHide={() => setConfirmKey(null)} centered>
          <Modal.Header closeButton>
            <Modal.Title>{activeConfirmAction.confirm.title}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {activeConfirmAction.confirm.body(activeConfirmItems.length)}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setConfirmKey(null)}
            >
              {activeConfirmAction.confirm.cancelLabel ?? 'Cancel'}
            </Button>
            <Button
              variant={activeConfirmAction.confirm.variant ?? activeConfirmAction.variant ?? 'primary'}
              onClick={handleConfirm}
            >
              {activeConfirmAction.confirm.confirmLabel ?? 'Confirm'}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
}

export default BulkActionToolbar;
